import logoSrc from "@/assets/images/anim-logo.png"
import orbSrc from "@/assets/images/anim-orb.png"
import colorVars from "@/assets/styles/colorVars"
import AttachmentSrc from "@/assets/svg/animation/attachment.inline.svg"
import ChevronDownSrc from "@/assets/svg/animation/chevron-down.svg"
import EmojiSrc from "@/assets/svg/animation/emoji.inline.svg"
import SendSrc from "@/assets/svg/animation/send.inline.svg"
import { useWindowSize } from "@/utilities/helpers"
import anime, { AnimeTimelineInstance } from "animejs"
import parse from "html-react-parser"
import { isNotEmpty } from "ramda-extension"
import React, { FC, useEffect, useRef, useState } from "react"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"

import { OrbsPath } from "./OrbsPath"
import {
  autoScrollMessagesList,
  generateSpannedWordMessage,
  resetAgentsAnim,
  resetScrollMessagesList,
} from "./helpers"

interface ITiming {
  aiThinkingTime: number
  messageReadabilityDelay: number
  messageTransition: number
}

interface IEmbed {
  description: string
  image: string
  price: string
  title: string
}

interface IMessage {
  embed?: IEmbed
  id?: number | string
  message?: string
  sender: "agent" | "user"
  thoughtProcesses?: Array<string>
}

interface IAgent {
  key: string
  name: string
}

export interface IHaawAnimation {
  agents: Array<IAgent>
  chat: {
    agentName: string
    avatar: string
    status: string
    user: string
  }
  messages: IMessage[]
}

export interface HowAiAgentsWorkProps {
  data: IHaawAnimation
}

// Styled components
const Wrapper = styled.div`
  position: relative;
  max-width: 1060px;
  height: 770px;
  margin: 0 auto;

  ${up("md")} {
    height: 600px;
  }
`
const ScalableBoundary = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: 100%;
  transform: scale(0.8);

  ${up("md")} {
    align-items: center;
  }

  ${up("lg")} {
    transform: scale(1);
  }
`

// Messages View - layout
const MessagesView = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 285px;
  height: 481px;
  background: ${colorVars.animations.messagesViewBg};
  border-radius: 16px;
  box-shadow: 0 0 200px 0 ${colorVars.animations.boxShadow1},
    0 0 34px 0 ${colorVars.animations.boxShadow2};
  opacity: 0;
`
const MessagesHeader = styled.div`
  display: flex;
  flex: 1 0 52px;
  gap: 0 10px;
  align-items: center;
  padding: 0 24px;
  background: ${colorVars.animations.boxShadow1};
  border-radius: 16px 16px 0 0;
`
const MessagesAvatarContainer = styled.div`
  position: relative;
  width: 32px;
  height: 32px;

  &::after {
    position: absolute;
    right: -1px;
    bottom: -1px;
    display: block;
    width: 10px;
    height: 10px;
    content: "";
    background: ${colorVars.animations.liveStatus};
    border: 2px solid ${colorVars.animations.darkBorder};
    border-radius: 10px;
  }
`
const MessagesAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 32px;
`
const MessagesHeaderItems = styled.div`
  display: flex;
  flex-direction: column;
`
const MessagesHeaderAgentName = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 18.4px;
  color: white;
`
const MessagesHeaderStatus = styled.span`
  font-size: 10px;
  line-height: 14px;
  color: ${colorVars.animations.transparentWhite1};
`
const MessagesListContainer = styled.div`
  height: 373px;
  overflow-y: clip;
`
const MessagesList = styled.div`
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  gap: 8px 0;
  height: 100%;
  padding: 12px;
  transition: transform 0.8s ease-in-out;
  transform: translateY(0);
`
const MessagesFooter = styled.div`
  display: flex;
  flex: 1 0 52px;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  margin-top: auto;
  border-top: 1px solid ${colorVars.animations.transparentWhite2};
`
const MessagesFooterInput = styled.span`
  font-size: 12.5px;
  line-height: 18.7px;
  color: ${colorVars.animations.transparentWhite1};
`
const MessagesFooterIcons = styled.div`
  display: flex;
  gap: 0 8px;

  > svg {
    width: 16px;
    height: 16px;
  }
`
const MessagesToggler = styled.div`
  position: absolute;
  bottom: -48px;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;

  &::before {
    display: block;
    width: 38px;
    height: 38px;
    margin: 12px 0 0 auto;
    content: "";
    background: ${colorVars.animations.transparentWhite3}
      url("${ChevronDownSrc}") center no-repeat;
    border-radius: 38px;
  }
`

// Messages View - bubbles
const MessageEntry = styled.div<any>`
  display: flex;
  flex-direction: column;
  gap: 8px 0;
  opacity: 0;
  transform-origin: 0% 0%;

  ${({ sender }) =>
    sender === "user" &&
    css`
      align-items: flex-end;
      transform-origin: 100% 0%;
    `}
`
const MessageAvatar = styled.img<IMessage>`
  width: 20px;
  height: 20px;
  border-radius: 20px;
  object-fit: cover;

  ${({ sender }) =>
    sender === "user" &&
    css`
      box-shadow: 0 0 0 2px ${colorVars.violet.p11};
    `}
`
const MessageBubble = styled.div<IMessage>`
  position: relative;
  max-width: 200px;
  min-height: 34px;
  padding: 8px 12px;
  background-color: ${colorVars.animations.messageBubble};
  border-radius: 0 8px 8px 8px;

  ${({ sender }) =>
    sender === "user"
      ? css`
          background-color: ${colorVars.animations.darkBorder};
          border-radius: 8px 0 8px 8px;

          ${MessageAiThinking} {
            display: none;
          }
        `
      : css`
          ${MessageAiThinking} {
            display: block;
            opacity: 1;
          }
        `}

  > p {
    margin: 0;
    font-size: 12px;
    line-height: 18px;
    color: ${colorVars.animations.platinum};
  }
`
const MessageGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 0 8px 8px 8px;
  opacity: 0;
  transform: translateZ(0) scale(1.001);

  &::before {
    position: absolute;
    top: -100px;
    left: -100px;
    z-index: 0;
    display: block;
    width: 400px;
    height: 400px;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    content: "";
    background-color: ${colorVars.animations.highlightGradientFrom};
    background-image: linear-gradient(
      ${colorVars.animations.highlightGradientFrom} 35%,
      ${colorVars.animations.highlightGradientTo} 60%
    );
    background-repeat: no-repeat;
    background-size: 100%;
    border-radius: 0 7px 7px 7px;
    transform: translate(-50% -50%);
    transform-origin: 50% 50%;
    animation: rotate 3000ms linear infinite;
  }

  &&::after {
    position: absolute;
    top: 1px;
    left: 1px;
    z-index: -1;
    z-index: 1;
    display: block;
    width: calc(100% - 2px);
    height: calc(100% - 2px);
    overflow: hidden;
    content: "";
    background: ${colorVars.animations.messageBubble};
    border-radius: 0 7px 7px 7px;
  }
`
const MessageAiThinking = styled.div`
  position: relative;
  top: -2px;
  z-index: 2;
  padding-left: 42px;
  font-size: 12px;
  line-height: 12px;
  color: ${colorVars.animations.platinum};

  &::before {
    content: "";
  }
`
const DotFlashingAnim = styled.div`
  /* Adapted from https://codepen.io/nzbin/pen/GGrXbp */
  position: relative;
  top: 8px;
  left: -32px;
  width: 6px;
  height: 6px;
  color: white;
  background-color: white;
  border-radius: 5px;
  animation: dot-flashing 0.5s infinite linear alternate;
  animation-delay: 0.25s;

  &::before,
  &::after {
    position: absolute;
    top: 0;
    display: inline-block;
    content: "";
  }

  &::before {
    left: -10px;
    width: 6px;
    height: 6px;
    color: white;
    background-color: white;
    border-radius: 5px;
    animation: dot-flashing 0.5s infinite alternate;
    animation-delay: 0s;
  }

  &::after {
    left: 10px;
    width: 6px;
    height: 6px;
    color: white;
    background-color: white;
    border-radius: 5px;
    animation: dot-flashing 0.5s infinite alternate;
    animation-delay: 0.5s;
  }

  @keyframes dot-flashing {
    0% {
      background-color: white;
    }
    50%,
    100% {
      background-color: ${colorVars.animations.transparentWhite3};
    }
  }
`
const MessageText = styled.p`
  position: relative;
  z-index: 2;
`
const Embed = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  width: 100%;
  height: 60px;
  overflow: hidden;
  background: ${colorVars.animations.transparentGrey1};
  border-radius: 6px;
  opacity: 0;
`
const EmbedImage = styled.img`
  flex: 0 0 60px;
  width: 60px;
  height: 60px;
  object-fit: cover;
`
const EmbedDetails = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  gap: 4px 0;
  justify-content: center;
  margin-left: 10px;
  font-size: 10px;
  line-height: 13px;
  color: white;
`
const EmbedTitle = styled.span`
  font-weight: 800;
`
const EmbedDescription = styled.span``
const EmbedPrice = styled.span``

// Agents View
const AgentsView = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
`
const LogoContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Logo = styled.div`
  position: absolute;
  z-index: 1;
  display: block;
  width: 55px;
  height: 55px;
  background: url("${logoSrc}") no-repeat center center;
  background-size: 55px 55px;
`
const LogoMainGlow = styled.div`
  position: absolute;
  z-index: -1;
  display: block;
  width: 200px;
  height: 200px;
  background: ${colorVars.pink.p11};
  filter: blur(22px);
  border-radius: 200px;
  transform: translateZ(0) scale(0.6);
  transform-origin: 50% 50%;
`
const LogoSideGlowWrapper = styled.div`
  position: absolute;
  display: block;
  width: 200px;
  height: 200px;
  transform-origin: 50% 50%;
  animation: rotate 18s linear infinite;
`
const LogoSideGlowContainer = styled.div`
  display: block;
  width: 100%;
  height: 100%;
`
const LogoGlowStyle = css`
  position: absolute;
  z-index: -2;
  display: block;
  width: 64px;
  height: 64px;
  background: ${colorVars.pink.p11};
  filter: blur(24px);
  border-radius: 64px;
  transform: translateZ(0);
`
const LogoGlowA = styled.div`
  ${LogoGlowStyle}

  top: 0;
  left: 0;
  transform: translate(20px, 20px);
`
const LogoGlowB = styled.div`
  ${LogoGlowStyle}

  right: 0;
  bottom: 0;
  transform: translate(-20px, -20px);
`
const Agent = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  padding: 20px;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: white;
  text-align: center;
  border: 1px solid ${colorVars.animations.transparentWhite1};
  border-radius: 140px;
  opacity: 0;
`
const Agents = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 600px;
  height: 600px;
  transform: translateY(5%);
  transform-origin: 50% 50%;

  ${Agent} {
    &:nth-child(2) {
      transform: translate(149.6px, 61.6px);
    }

    &:nth-child(3) {
      transform: translate(211.2px, 211.2px);
    }

    &:nth-child(4) {
      transform: translate(149.6px, 360.8px);
    }

    &:nth-child(5) {
      transform: translate(0, 422.4px);
    }

    &:nth-child(6) {
      transform: translate(-149.6px, 360.8px);
    }

    &:nth-child(7) {
      transform: translate(-211.2px, 211.2px);
    }

    &:nth-child(8) {
      transform: translate(-149.6px, 61.6px);
    }
  }
`
const Orb = styled.div`
  position: absolute;
  display: block;
  width: 24px;
  height: 24px;
  background: url("${orbSrc}") no-repeat center center;
  background-size: 24px 24px;
`
const Orbs = styled.div`
  position: absolute;
  opacity: 0;
  transform: scale(0);

  ${Orb} {
    &:nth-child(1) {
      transform: translate(0, 0);
    }

    &:nth-child(2) {
      transform: translate(65.31px, 26.88px);
    }

    &:nth-child(3) {
      transform: translate(92.2px, 92.2px);
    }

    &:nth-child(4) {
      transform: translate(65.31px, 157.41px);
    }

    &:nth-child(5) {
      transform: translate(0, 184.31px);
    }

    &:nth-child(6) {
      transform: translate(-65.31px, 157.41px);
    }

    &:nth-child(7) {
      transform: translate(-92.2px, 92.2px);
    }

    &:nth-child(8) {
      transform: translate(-65.31px, 26.88px);
    }
  }
`
const OrbsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 200px;
  height: 200px;
  transform-origin: 49% 50%;

  &.animate-rotation {
    animation: rotate 3s linear infinite;

    @keyframes rotate {
      0% {
        transform: rotate(0deg);
      }

      100% {
        transform: rotate(360deg);
      }
    }
  }
`

// Agents View: orb flyout animation instances
const OrbPathAnimation = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 890px;
  height: 890px;

  ${up("md")} {
    width: 690px;
    height: 690px;
  }

  ${up("lg")} {
    width: 554px;
    height: 557px;
  }
`

const AnimatableOrbContainer = styled.div<any>`
  position: absolute;
  display: block;
  width: 550px;
  height: 550px;

  ${up("md")} {
    width: 550px;
    height: 550px;
  }

  ${up("lg")} {
    width: 554px;
    height: 557px;
  }
`
const AnimatableOrb = styled(Orb)`
  position: absolute;
  top: -12px;
  left: -12px;
  opacity: 0;
`

export const AnimationPlayer: FC<HowAiAgentsWorkProps> = ({ data }) => {
  const { agents, chat, messages } = data

  // Refs
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const masterTimelineRef = useRef<AnimeTimelineInstance | null>(null)
  const messagesTimelineRef = useRef<AnimeTimelineInstance | null>(null)
  const agentsTimelineRef = useRef<AnimeTimelineInstance | null>(null)
  const messagesViewRef = useRef<HTMLDivElement | null>(null)
  const agentsViewRef = useRef<HTMLDivElement | null>(null)
  const logoRef = useRef<HTMLDivElement | null>(null)
  const logoMainGlowRef = useRef<HTMLDivElement | null>(null)
  const logoSideGlowRef = useRef<HTMLDivElement | null>(null)
  const agentsRef = useRef<HTMLDivElement | null>(null)
  const orbsRef = useRef<HTMLDivElement | null>(null)
  const orbsContainerRef = useRef<HTMLDivElement | null>(null)
  const messagesListRef = useRef<HTMLDivElement | null>(null)
  const orbPathAnimationRef = useRef<HTMLDivElement | null>(null)
  const animatableOrbContainerRef = useRef<HTMLDivElement | null>(null)

  // State
  const [replayKey, setReplayKey] = useState<number>(0)

  // Message timing
  const timing: ITiming = {
    aiThinkingTime: 7000,
    messageReadabilityDelay: 2000,
    messageTransition: 300,
  }

  // Window size hook
  const windowWidth = useWindowSize().width

  // Intersection observer

  // Effects
  useEffect(() => {
    // Timers
    let subTimelineHandoffTimer: number

    // Responsiveness
    const isMobile = windowWidth < 768

    // Master timeline intialization
    masterTimelineRef.current = anime.timeline({
      autoplay: false,
      complete: () => {
        setReplayKey((prev) => prev + 1)
        masterTimelineRef.current?.pause()
      },
      easing: "easeInOutSine",
      loop: false,
    })

    // Master timeline events
    masterTimelineRef.current
      // Fade-in AgentsView
      .add({
        duration: 700,
        opacity: [0, 1],
        scale: [0.75, isMobile ? 0.8 : 1],
        targets: agentsViewRef.current,
        translateY: isMobile ? 200 : 0,
      })
      // Fade-in LogoMainGlow
      .add(
        {
          duration: 1400,
          opacity: [0, 1],
          targets: logoMainGlowRef.current,
        },
        "-=500",
      )
      // Scale-down Logo
      .add(
        {
          duration: 800,
          scale: [1, 0.75],
          targets: logoRef.current,
        },
        "-=700",
      )
      // Fade-in and rotate Orbs linearly
      .add(
        {
          begin: () =>
            orbsContainerRef.current?.classList.add("animate-rotation"),
          easing: "linear",
          keyframes: [
            {
              duration: 800,
              opacity: 0,
              scale: 0,
            },
            {
              delay: 200,
              duration: 400,
              opacity: 1,
              scale: 1,
            },
            {
              duration: 3200,
              opacity: 1,
              scale: 1,
            },
            {
              duration: 1200,
              opacity: 0,
              scale: 0,
            },
          ],
          targets: orbsRef.current,
        },
        "-=1000",
      )
      // Fade-in Agents, staggered
      .add(
        {
          delay: anime.stagger(140),
          duration: 700,
          opacity: [0, 1],
          targets: agentsRef.current?.childNodes,
        },
        "-=3200",
      )
      // Fade-in and scale up LogoSideGlowContainer
      .add(
        {
          duration: 1400,
          opacity: [0, 1],
          scale: [0, 0.8],
          targets: logoSideGlowRef.current,
        },
        "-=4000",
      )
      // Partially fade-out Agents
      .add(
        {
          delay: 800,
          duration: 800,
          opacity: [1, 0.6],
          targets: agentsRef.current?.childNodes,
        },
        "-=200",
      )
      // Move AgentsView to the right if it's on Desktop
      .add(
        {
          duration: 2000,
          easing: "easeInOutSine",
          targets: agentsViewRef.current,
          translateX: isMobile ? 0 : 180,
          translateY: isMobile ? 800 : 0,
        },
        "-=1000",
      )
      // Move MessagesView from offscreen to the left if desktop, from the center to the bottom if mobile
      .add(
        {
          complete: () => {
            subTimelineHandoffTimer = window.setTimeout(() => {
              messagesTimelineRef.current?.play()
              masterTimelineRef.current?.pause()
            }, 450)
          },
          duration: 2000,
          easing: "easeInOutSine",
          keyframes: [
            {
              opacity: 0,
              scale: 0.85,
              translateX: isMobile ? 0 : -1200,
              translateY: 0,
            },
            {
              opacity: 1,
              scale: 1,
              translateX: isMobile ? 0 : -280,
              translateY: isMobile ? -70 : 0,
            },
          ],
          targets: messagesViewRef.current,
        },
        "-=2300",
      )
      // After completing all sub-timelines, the animation is returned HERE
      // Move MessagesView off-screen to the left
      .add({
        delay: 100,
        duration: 2000,
        easing: "easeInOutSine",
        keyframes: [
          {
            opacity: 1,
            scale: 1,
            translateX: isMobile ? 0 : -280,
          },
          {
            opacity: 0,
            scale: 0.85,
            translateX: isMobile ? 0 : -1200,
          },
        ],
        targets: messagesViewRef.current,
      })
      // Move AgentsView to the center
      .add(
        {
          duration: 2000,
          easing: "easeInOutSine",
          keyframes: [
            {
              translateX: isMobile ? 0 : 180,
            },
            {
              translateX: 0,
            },
          ],
          targets: agentsViewRef.current,
        },
        "-=2000",
      )
      // Hide all Agents
      .add(
        {
          duration: 800,
          opacity: [0.6, 0],
          targets: agentsRef.current?.childNodes,
        },
        "-=200",
      )
      // Hide everything
      .add(
        {
          complete: () => {
            // Reset everything to replay animation
            setReplayKey((prev) => prev + 1)
            masterTimelineRef.current?.pause()
          },
          duration: 800,
          opacity: [1, 0],
          targets: agentsViewRef.current,
        },
        "-=200",
      )

    // Messages sub-timeline intialization
    messagesTimelineRef.current = anime.timeline({
      autoplay: false,
      easing: "easeInOutSine",
      loop: false,
    })

    // Handle individual message reveals after base timeline setup
    messages.forEach((message, index) => {
      const isAiMessage = message.sender === "agent"
      const currentMessage: any = messagesListRef.current?.childNodes[index]
      const currentMessageBubble = `#haaw_message_bubble_${index}`
      const currentMessageText = document.querySelector(
        `${currentMessageBubble} .message-text`,
      )

      const currentMessageHeight =
        message.embed && isNotEmpty(message.embed)
          ? 164
          : (currentMessageText?.getBoundingClientRect().height ?? 300) + 32

      // IF it's an AI-generated message, add Thinking delay
      if (isAiMessage) {
        messagesTimelineRef.current
          // Ensure answer remains hidden
          ?.add({
            duration: 10,
            opacity: 0,
            targets: `${currentMessageBubble} .message-text span, ${currentMessageBubble} .message-embed`,
          })
          // Reduce max width for thinking animation
          .add({
            duration: 10,
            maxHeight: 34,
            maxWidth: 50,
            targets: currentMessageBubble,
          })
          // Fade-in current message
          .add({
            complete: () => {
              if (
                isNotEmpty(message.thoughtProcesses) &&
                isNotEmpty(message.message) &&
                message.sender === "agent"
              ) {
                handleAgentAnimation(message.thoughtProcesses)
              }
            },
            delay: 500,
            duration: 500,
            opacity: [0, 1],
            targets: currentMessage,
          })
          // Enable glow effect
          .add({
            delay: 50,
            duration: 200,
            opacity: [0, 1],
            targets: `${currentMessageBubble} .message-glow`,
          })
          // Fade out thinking message
          .add(
            {
              delay: 500,
              duration: 500,
              height: 0,
              opacity: [1, 0],
              targets: `${currentMessageBubble} .message-thinking`,
            },
            `+=${timing.aiThinkingTime / 2}`,
          )
          // Set max width and height back to normal
          .add({
            delay: 200,
            duration: 500,
            maxHeight: currentMessageHeight,
            maxWidth: 200,
            targets: currentMessageBubble,
          })
          // Reveal word for word effect (AI slower answer speed)
          .add(
            {
              delay: anime.stagger(50),
              duration: 125,
              opacity: 1,
              targets: `${currentMessageBubble} p span`,
            },
            "-=300",
          )
          // If the message contains an embed, reveal it
          .add(
            {
              duration: 500,
              opacity: 1,
              targets: ".message-embed",
            },
            "-=300",
          )
      }

      // Initialize message with fade-in and scale-up
      messagesTimelineRef.current
        ?.add({
          duration: timing.messageTransition * 2,
          opacity: isAiMessage ? 1 : [0, 1],
          scale: isAiMessage ? [1, 1.5] : [0.6, 1.5],
          targets: currentMessage,
        })
        // Readability: change the current message's background color
        .add(
          !isAiMessage
            ? {
                backgroundColor: colorVars.animations.userHighlight,
                duration: timing.messageTransition * 2,
                targets: currentMessage?.childNodes[1],
              }
            : {},
          `-=${timing.messageTransition}`,
        )
        // Readability: scale up the current message for a few seconds and scale it back down
        .add({
          complete: () => {
            // Hide message glow (we're out of the loop at this stage, and the animation cannot be held back)
            anime({
              duration: 200,
              easing: "easeInOutSine",
              opacity: [1, 0],
              targets: `${currentMessageBubble} .message-glow`,
            })
            if (isAiMessage && isNotEmpty(message.thoughtProcesses)) {
              agentsTimelineRef.current?.play()
            }
          },
          delay: timing.messageTransition,
          keyframes: [
            {
              duration: timing.messageReadabilityDelay,
              scale: 1.5,
            },
            {
              duration: timing.messageTransition,
              scale: [1.5, 1],
            },
          ],
          targets: currentMessage,
        })
        // Restore the current message's background color
        .add(
          {
            backgroundColor: isAiMessage
              ? colorVars.animations.messageBg1
              : colorVars.animations.messageBg2,
            complete: () => {
              // Evaluate if there's a need to scroll the messages list
              if (
                (index === 0 || index <= messages.length - 3) &&
                messages.length > 2
              ) {
                // Call the scroller helper function
                autoScrollMessagesList(messagesListRef.current)
              }

              // If this is the last message, pause this sub-timeline and resume the master timeline
              if (index === messages.length - 1) {
                messagesTimelineRef.current?.pause()
                masterTimelineRef.current?.play()
              }
            },
            duration: timing.messageTransition * 2,
            targets: currentMessage?.childNodes[1],
          },
          `-=${timing.messageTransition}`,
        )
    })

    // Append agent animations to agents sub-timeline
    const handleAgentAnimation = (thoughtProcesses?: Array<IAgent["key"]>) => {
      // Agents sub-timeline initialization
      agentsTimelineRef.current = anime.timeline({
        autoplay: false,
        easing: "easeInOutSine",
        loop: false,
      })

      thoughtProcesses?.forEach((process, index) => {
        const currentOrb = `#animatable_orb_${index}`

        // Generate Anime.js paths
        const pathId = `#svg_orbs_path #orbs_path_${process}`
        const path = anime.path(pathId)

        // For each kind of thought process, animate an orb using a path
        agentsTimelineRef.current
          // Fade in and enlarge the orb
          ?.add(
            {
              duration: 500,
              opacity: [0, 1],
              targets: currentOrb,
              translateX: 0,
              translateY: 0,
            },
            index > 0 ? "-=2300" : "",
          )
          // Animate the orb along the path
          .add(
            {
              direction: "forward",
              duration: 2600,
              easing: "linear",
              loop: false,
              targets: currentOrb,
              translateX: path("x"),
              translateY: path("y"),
            },
            index > 0 ? "-=2300" : "-=500",
          )
          // Fully fade-in the corresponding agent
          .add(
            {
              duration: 600,
              opacity: [0.6, 1],
              targets: `#agent_${process}`,
            },
            `-=${2300 * (index + 1)}`,
          )
          // Hide out orb
          .add(
            {
              duration: 300,
              opacity: [1, 0],
              targets: currentOrb,
            },
            `-=${100 * (index + 1)}`,
          )
          // Fade out the corresponding agent
          .add(
            {
              duration: 300,
              opacity: [1, 0.6],
              targets: `#agent_${process}`,
            },
            `-=${300 * (index + 1)}`,
          )
      })

      // When the AI answer is revealed, enlarge the logo and its glows
      agentsTimelineRef.current
        ?.add(
          {
            delay: 1000,
            duration: 750,
            scale: [0.6, 0.9],
            targets: logoMainGlowRef.current,
          },
          "+=100",
        )
        // Enlarge logo glows A and B
        .add(
          {
            duration: 750,
            scale: 1.3,
            targets: logoSideGlowRef.current,
          },
          "-=750",
        )
        // Make the logo bigger
        .add(
          {
            complete: () => agentsTimelineRef.current?.pause(),
            duration: 750,
            scale: 1.1,
            targets: logoRef.current,
          },
          "-=750",
        )
        // Restore the logo's original size
        .add({
          delay: 1000,
          duration: 750,
          scale: 0.75,
          targets: logoRef.current,
        })
        // Restore the logo glows' original size
        .add(
          {
            duration: 750,
            scale: 0.75,
            targets: logoSideGlowRef.current,
          },
          "-=750",
        )
        // Restore the logo main glow's original size
        .add(
          {
            complete: () => {
              // Self-destruct
              agentsTimelineRef.current?.pause()
              agentsTimelineRef.current = null
            },
            duration: 750,
            scale: [0.9, 0.6],
            targets: logoMainGlowRef.current,
          },
          "-=750",
        )

      // Playback the agents timeline
      agentsTimelineRef.current?.play()
    }

    // Startup master timeline animation - its events control sub-timelines
    masterTimelineRef.current.play()

    // Cleanup
    return () => {
      // Remove timers
      window.clearTimeout(subTimelineHandoffTimer)

      // Disable and invalidate timeline refs
      masterTimelineRef.current?.pause()
      masterTimelineRef.current = null
      messagesTimelineRef.current?.pause()
      messagesTimelineRef.current = null
      agentsTimelineRef.current?.pause()
      agentsTimelineRef.current = null

      // Reset modified attributes (scroll, orbs)
      resetScrollMessagesList(messagesListRef.current)
      resetAgentsAnim(animatableOrbContainerRef.current, agentsRef.current)
    }
  }, [replayKey, windowWidth])

  return (
    <Wrapper key={replayKey} ref={wrapperRef}>
      <ScalableBoundary>
        <MessagesView ref={messagesViewRef}>
          <MessagesHeader>
            {chat.avatar && (
              <MessagesAvatarContainer>
                <MessagesAvatar src={chat.avatar} />
              </MessagesAvatarContainer>
            )}
            <MessagesHeaderItems>
              {chat.agentName && (
                <MessagesHeaderAgentName>
                  {chat.agentName}
                </MessagesHeaderAgentName>
              )}
              {chat.status && (
                <MessagesHeaderStatus>{chat.status}</MessagesHeaderStatus>
              )}
            </MessagesHeaderItems>
          </MessagesHeader>
          <MessagesListContainer>
            <MessagesList ref={messagesListRef}>
              {messages.map((message, index) => (
                <MessageEntry
                  key={`message-entry-${index}`}
                  sender={message.sender}>
                  <MessageAvatar
                    sender={message.sender}
                    src={message.sender === "user" ? chat.user : chat.avatar}
                  />
                  <MessageBubble
                    id={`haaw_message_bubble_${index}`}
                    sender={message.sender}>
                    {message.sender === "agent" && (
                      <MessageGlow className="message-glow" />
                    )}
                    <MessageAiThinking className="message-thinking">
                      <DotFlashingAnim />
                      Thinking...
                    </MessageAiThinking>
                    <MessageText className="message-text">
                      {parse(generateSpannedWordMessage(message.message))}
                    </MessageText>
                    {message.embed && isNotEmpty(message.embed) && (
                      <Embed className="message-embed">
                        {message.embed.image && (
                          <EmbedImage
                            alt={message.embed.title}
                            src={message.embed.image}
                          />
                        )}
                        <EmbedDetails>
                          {message.embed.title && (
                            <EmbedTitle>{message.embed.title}</EmbedTitle>
                          )}
                          {message.embed.description && (
                            <EmbedDescription>
                              {message.embed.description}
                            </EmbedDescription>
                          )}
                          {message.embed.price && (
                            <EmbedPrice>{message.embed.price}</EmbedPrice>
                          )}
                        </EmbedDetails>
                      </Embed>
                    )}
                  </MessageBubble>
                </MessageEntry>
              ))}
            </MessagesList>
          </MessagesListContainer>
          <MessagesFooter>
            <MessagesFooterInput>Write a reply</MessagesFooterInput>
            <MessagesFooterIcons>
              <EmojiSrc />
              <AttachmentSrc />
              <SendSrc />
            </MessagesFooterIcons>
          </MessagesFooter>
          <MessagesToggler />
        </MessagesView>
        <AgentsView ref={agentsViewRef}>
          <LogoContainer>
            <Logo ref={logoRef} />
            <LogoMainGlow ref={logoMainGlowRef} />
            <LogoSideGlowWrapper>
              <LogoSideGlowContainer ref={logoSideGlowRef}>
                <LogoGlowA />
                <LogoGlowB />
              </LogoSideGlowContainer>
            </LogoSideGlowWrapper>
          </LogoContainer>
          <Agents ref={agentsRef}>
            {agents.map((agent, index) => (
              <Agent id={`agent_${agent.key}`} key={index}>
                {agent.name}
              </Agent>
            ))}
          </Agents>
          <OrbPathAnimation ref={orbPathAnimationRef}>
            <OrbsPath />
            <AnimatableOrbContainer ref={animatableOrbContainerRef}>
              {Object.keys(agents).map((_, index) => (
                <AnimatableOrb id={`animatable_orb_${index}`} key={index} />
              ))}
            </AnimatableOrbContainer>
          </OrbPathAnimation>
          <Orbs ref={orbsRef}>
            <OrbsContainer ref={orbsContainerRef}>
              {agents.map((_, index) => (
                <Orb key={index}></Orb>
              ))}
            </OrbsContainer>
          </Orbs>
        </AgentsView>
      </ScalableBoundary>
    </Wrapper>
  )
}
